import * as React from "react";
import { forceCheck } from "react-lazyload";
import { useLocation } from "react-router-dom";
import { observer } from "mobx-react";

import { UitkTab, UitkTabs, UitkTabsType } from "uitk-react-tabs";
import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkHeading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";
import { classNames } from "uitk-react-utils";

import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { InsideTabGroupContext } from "components/shared/InsideTabGroupContext/InsideTabGroupContext";
import LinksGridWithDialog from "components/flexComponents/Links/GridView/LinksGridWithDialog";
import {
  PageBackgroundContext,
  BackgroundColorTokens,
} from "src/components/flexComponents/PageLevelProviders/PageBackgroundColor/PageBackgroundContext";
import { TabGroupProps, TabGroupFTNode, TabGroupFlexModuleResult } from "./typings";
import { hasRenderableChilren } from "src/components/flexFramework/FlexTemplateNode";
import { BlossomComponent } from "src/components/flexFramework/domain/BlossomComponent";
import TabContents from "../TabContents/TabContents";
import { withStores } from "stores";

export const TabGroup = withStores(
  "analytics",
  "flexModuleModelStore",
  "context"
)(
  observer((props: TabGroupProps) => {
    const { flexTemplateNode, flexModuleModelStore, context, blossomComponent } = props;

    if (!blossomComponent) {
      return null;
    }

    const { id, options } = flexTemplateNode;
    if (!options) {
      return null;
    }

    const { fmTitleId, fmId, view } = options;

    const model = flexModuleModelStore.getModel(id) as TabGroupFlexModuleResult;

    if (!hasRenderableChilren(flexTemplateNode, flexModuleModelStore)) {
      return null;
    }

    const blossomComponentChildren = blossomComponent.getBlossomComponents();

    /**
     * inspects provided components and returns a de-duped array containing only TAB type components that have children
     * @param components
     * @returns BlossomComponent[]
     */

    const filterTabs = (components: BlossomComponent[]): BlossomComponent[] => {
      // Tabs cannot have the same name and must contain at least 1 valid module
      const tabNames = new Set();

      return components.filter((component) => {
        const blossomComponentFlexTemplateNode = component.flexTemplateNode as TabGroupFTNode;
        const localOptions = flexTemplateNode.options;
        // Verify the items in children are tabs, tab name isn't duplicated, and tab has modules
        if (
          !blossomComponentFlexTemplateNode.view ||
          (blossomComponentFlexTemplateNode.view && blossomComponentFlexTemplateNode.view !== "tab") ||
          !component.name ||
          tabNames.has(component.name) ||
          !blossomComponentFlexTemplateNode.childNodes
        ) {
          return false;
        }

        // Verify the number of valid modules, there's no way to know if the render function of a component
        // will return a null value, so the decision is based on other metadata, as well as verifying the module is supported.
        if (!hasRenderableChilren(blossomComponentFlexTemplateNode, flexModuleModelStore)) {
          return false;
        }

        tabNames.add(localOptions.flexComponentName);

        return true;
      });
    };

    const handleTabSelect = (currentTabIndex: number): void => {
      if (typeof currentTabIndex !== "number") {
        return;
      }

      const currentTab = filteredTabs[currentTabIndex];

      if (!currentTab) {
        return;
      }

      const trackingInfo: FlexTrackingInfo = {
        moduleName: id,
        action: Action.CLICK,
        rfrr: `tab.${currentTabIndex}`,
      };
      sendDelayedTrackEvent(trackingInfo, props.analytics);
      setSelectedTab(currentTabIndex);
    };

    const filteredTabs = filterTabs(blossomComponentChildren);
    if (!filteredTabs.length) {
      return null;
    }

    const defaultTabIndex = 0;
    const [selectedTab, setSelectedTab] = React.useState(defaultTabIndex);

    const { hash } = useLocation();

    React.useEffect(() => {
      if (hash.slice(0, 12) === "#section-tab") {
        const anchorId = `tab-${hash.slice(9)}`;
        const tabAnchor = document.getElementById(anchorId)?.children;
        const tabContent = tabAnchor && tabAnchor[0].textContent;

        if (tabAnchor && tabContent) {
          const filteredTabNames = filteredTabs.map((tab) => tab.name);
          filteredTabNames.includes(tabContent) && setSelectedTab(filteredTabNames.indexOf(tabContent));
          tabAnchor[0].scrollIntoView();
        }
      }
    }, [hash]);

    React.useEffect(() => {
      // Forces the lazy loaded images check to see if they need to be loaded
      // when the current tab changes
      forceCheck();
    }, [selectedTab]);

    const keyHelper = new ItemKeyHelper("tab");
    const { title, dynamicHeight, boxType } = model;
    const tabGroupClasses = classNames("TabGroup uitk-cell all-cell-1-1", {
      affinityTabGroup: view === "affinity",
      dynamicHeightTabGroup: dynamicHeight,
    });

    if (view === "row-trigger" || view === "standard-link") {
      const linksCardView = view === "standard-link" && filteredTabs.length === 1 ? "no-card" : "";

      return (
        <div
          className={`${tabGroupClasses} ${linksCardView}`}
          data-fm={options.fmId}
          data-fm-title-id={options.fmTitleId}
        >
          <UitkCard>
            <UitkCardContentSection padded={false}>
              <LinksGridWithDialog view={view} groups={filteredTabs} id={id} context={context} />
            </UitkCardContentSection>
          </UitkCard>
        </div>
      );
    }

    const backgroundColor = React.useContext(PageBackgroundContext);
    const withBorder = backgroundColor === BackgroundColorTokens.BACKGROUND_WHITE;
    const uitkCardClasses = classNames({ cardBorder: boxType === "bordered-cards" });

    return (
      <UitkSpacing padding={{ inline: "three" }}>
        <div className={tabGroupClasses} data-fm={fmId} data-fm-title-id={fmTitleId}>
          <UitkCard border={withBorder} className={uitkCardClasses}>
            <UitkCardContentSection>
              <UitkHeading tag="h2" size={4}>
                {title}
              </UitkHeading>

              <div className="uitk-cell">
                <InsideTabGroupContext.Provider value>
                  <UitkTabs selectedTab={selectedTab} onTabSelect={handleTabSelect} tabsType={UitkTabsType.NATURAL}>
                    {filteredTabs.map((tab, index) => {
                      const tabFlexTemplateNode = tab.flexTemplateNode as TabGroupFTNode;
                      return (
                        <UitkTab
                          name={
                            tabFlexTemplateNode.options.containerName || tabFlexTemplateNode.options.flexComponentName
                          }
                          key={keyHelper.next()}
                          targetURI="#"
                          tabLinkAttr={{
                            id: `tab-${flexTemplateNode.id}-tab-${index + 1}`,
                            "data-section-id": `#section-${flexTemplateNode.id}-tab-${index + 1}`,
                          }}
                        >
                          <TabContents context={context} blossomComponent={tab} />
                        </UitkTab>
                      );
                    })}
                  </UitkTabs>
                </InsideTabGroupContext.Provider>
              </div>
            </UitkCardContentSection>
          </UitkCard>
        </div>
      </UitkSpacing>
    );
  })
);

export default TabGroup;
